import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { resolveGuardAction, SSOAttemptKey, LoginRedirectKey } from './guard'
import { recoverRouteLoad } from './loadRecovery'

export { SSOAttemptKey, LoginRedirectKey }

const routes: RouteRecordRaw[] = [
  {
    path: '/',
    component: () => import('@/layouts/PublicLayout.vue'),
    children: [
      { path: '', name: 'home', component: () => import('@/pages/Home.vue') },
      {
        path: ':pathMatch(.*)*',
        name: 'not-found',
        component: () => import('@/pages/NotFound.vue'),
      },
    ],
  },
  {
    path: '/account',
    component: () => import('@/layouts/AccountLayout.vue'),
    meta: { requiresAuth: true },
    children: [
      { path: '', name: 'account', redirect: { name: 'account-deployments' } },
      {
        path: 'profile',
        name: 'account-profile',
        component: () => import('@/pages/account/Profile.vue'),
      },
      {
        path: 'deployments',
        name: 'account-deployments',
        component: () => import('@/pages/account/deployments/List.vue'),
      },
      {
        path: 'deployments/new',
        name: 'account-deployment-new',
        component: () => import('@/pages/account/deployments/New.vue'),
      },
      {
        path: 'deployments/:id',
        name: 'account-deployment-detail',
        component: () => import('@/pages/account/deployments/Detail.vue'),
        props: true,
      },
      {
        path: 'libraries',
        name: 'account-libraries',
        component: () => import('@/pages/account/libraries/List.vue'),
      },
      {
        path: 'billing',
        name: 'account-billing',
        component: () => import('@/pages/account/WorkspaceBilling.vue'),
      },
      {
        path: 'billing/history',
        name: 'account-payment-history',
        component: () => import('@/pages/account/WorkspaceBilling.vue'),
      },
      {
        path: 'billing/:name',
        name: 'org-billing',
        component: () => import('@/pages/account/orgs/tabs/Billing.vue'),
        props: true,
      },
      {
        path: 'billing/:name/history',
        name: 'org-payment-history',
        component: () => import('@/pages/account/orgs/tabs/PaymentHistory.vue'),
        props: true,
      },
      {
        path: 'security',
        name: 'account-security',
        component: () => import('@/pages/account/Security.vue'),
      },
      {
        path: 'security/password',
        name: 'account-security-password',
        component: () => import('@/pages/account/security/Password.vue'),
      },
      {
        path: 'security/two-factor',
        name: 'account-security-two-factor',
        component: () => import('@/pages/account/security/TwoFactor.vue'),
      },
      {
        path: 'security/linked-accounts',
        name: 'account-security-linked-accounts',
        component: () => import('@/pages/account/security/LinkedAccounts.vue'),
      },
      {
        path: 'sessions',
        name: 'account-sessions',
        component: () => import('@/pages/account/Sessions.vue'),
      },
      // /account/delete folded into the Profile page's Danger zone.
      { path: 'delete', redirect: { name: 'account-profile' } },
    ],
  },
]

export const router = createRouter({
  history: createWebHistory(),
  routes,
})

router.onError((error, to) => recoverRouteLoad(error, to.fullPath))

router.beforeEach(async (to) => {
  const auth = useAuthStore()
  // Always hydrate auth state on first navigation so the header renders
  // the right side (login button vs user menu) without a second round-trip.
  await auth.ensureLoaded()
  // A transient /auth/me failure is not proof of logout. Let the app mount
  // while the bounded store retry recovers instead of starting a login loop.
  if (auth.isUnavailable) return true

  const hasWindow = typeof window !== 'undefined'
  const action = resolveGuardAction({
    isAuthenticated: auth.isAuthenticated,
    requiresAuth: to.meta.requiresAuth === true,
    fullPath: to.fullPath,
    getFlag: (key) => hasWindow && sessionStorage.getItem(key) === '1',
    setFlag: (key) => {
      if (hasWindow) sessionStorage.setItem(key, '1')
    },
    clearFlag: (key) => {
      if (hasWindow) sessionStorage.removeItem(key)
    },
  })

  if (action.kind === 'navigate') {
    if (hasWindow) window.location.assign(action.url)
    return false
  }
  return true
})

// Per-route robots meta. Authenticated surfaces stay noindex — there's
// nothing for a crawler to see and indexing session-shaped URLs is pure
// downside. Public pages (home, 404) drop the tag so Lighthouse SEO can
// grade them properly instead of flagging "blocked from indexing".
router.afterEach((to) => {
  const shouldBlock = to.meta.requiresAuth === true
  let meta = document.head.querySelector<HTMLMetaElement>('meta[name="robots"]')
  if (shouldBlock) {
    if (!meta) {
      meta = document.createElement('meta')
      meta.name = 'robots'
      document.head.appendChild(meta)
    }
    meta.content = 'noindex'
  } else if (meta) {
    meta.remove()
  }
})
